import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { Diagram, Legend, type ArchGraph } from "../components/diagram";
import { city, relative } from "../components/ui";

// The question: how does the whole lab fit together in AWS? Route 53 → the lab host (Caddy and
// every project's compose project) → the shared RDS (one database each) → SSM, Secrets Manager,
// the artifacts bucket and CloudWatch; and every environment still outside the lab, in its region,
// with what AWS says it consists of.

interface Place {
  account: string | null;
  region: string;
  vpcId: string | null;
  host: { id: string | null; name: string; type: string | null; ip: string | null };
  database: { id: string; engine: string | null; version: string | null; class: string | null };
  zone: { id: string; name: string };
  artifactBucket: string | null;
}
interface SystemView {
  checkedAt: string;
  place: Place;
  graph: ArchGraph;
  lab: { project: string; projectName: string; env: string; hosts: string[]; services: number; database: string | null; available: boolean }[];
  external: {
    project: string;
    projectName: string;
    env: string;
    region: string;
    urls: string[];
    error: string | null;
    counts: { instances: number; services: number; loadBalancers: number; distributions: number; databases: number; buckets: number; names: number } | null;
    errors: string[];
  }[];
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

export function SystemPage() {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const q = useQuery({ queryKey: ["system"], queryFn: () => api<SystemView>("/system"), staleTime: 5 * 60_000 });
  const refresh = async () => {
    setRefreshing(true);
    try {
      qc.setQueryData(["system"], await api<SystemView>("/system?refresh=1"));
    } finally {
      setRefreshing(false);
    }
  };
  const d = q.data;

  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>System</h1>
          <p className="sub">
            {q.isPending ? <span className="skeleton" style={{ width: 360, height: 16 }} />
              : q.error ? "Couldn't read the system."
              : `${plural(d!.lab.length, "environment")} on the lab host, ${d!.external.length} still outside it, in one AWS account${d!.place.account ? ` (${d!.place.account})` : ""}.`}
          </p>
        </div>
        {d && (
          <div className="actions">
            <span className="stamp">Read {relative(d.checkedAt)} · kept 10 min</span>
            <button className="btn" onClick={refresh} disabled={refreshing}>{refreshing ? "Reading AWS…" : "Read again"}</button>
          </div>
        )}
      </div>

      {q.isPending && <div className="panel"><span className="skeleton" style={{ width: "100%", height: 420 }} /><p className="small muted" style={{ marginTop: 8 }}>Reading every release and AWS; the first read takes up to a minute.</p></div>}
      {q.error && <div className="panel error">{q.error.message} <button className="btn ghost" onClick={() => q.refetch()}>Retry</button></div>}

      {d && (
        <>
          {d.graph.warnings.length > 0 && (
            <section className="panel flush" aria-label="Warnings">
              <div className="list">
                {d.graph.warnings.map((w) => (
                  <div className="item" key={w} style={{ gridTemplateColumns: "auto minmax(0, 1fr)" }}><span className="pill negative">Warning</span><span>{w}</span></div>
                ))}
              </div>
            </section>
          )}
          <section className="panel flush" aria-label="System design">
            <Diagram g={d.graph} label="System design of the lab and the environments outside it" />
            <div style={{ padding: "4px 20px 16px" }}><Legend /></div>
          </section>

          <div className="split">
            <section className="panel flush" aria-labelledby="on-lab">
              <h2 id="on-lab" style={{ padding: "16px 20px 8px" }}>On the lab host</h2>
              <p className="small muted" style={{ padding: "0 20px 12px" }}>
                EC2 <span className="mono">{d.place.host.id ?? d.place.host.name}</span>{d.place.host.type ? ` · ${d.place.host.type}` : ""}{d.place.host.ip ? ` · ${d.place.host.ip}` : ""} in {city(d.place.region)};
                RDS <span className="mono">{d.place.database.id}</span>{d.place.database.class ? ` · ${d.place.database.class}` : ""}{d.place.database.version ? ` · PostgreSQL ${d.place.database.version}` : ""}.
              </p>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Environment</th><th>Names</th><th className="num">Services</th><th className="hide-phone">Database</th></tr></thead>
                  <tbody>
                    {d.lab.map((e) => (
                      <tr key={`${e.project}/${e.env}`}>
                        <td><Link to={`/p/${e.project}?tab=architecture&env=${encodeURIComponent(e.env)}`} className="cell-main">{e.projectName}</Link><div className="cell-sub mono">{e.env}</div></td>
                        <td className="mono small" style={{ overflowWrap: "anywhere" }}>{e.hosts.join(", ") || <span className="muted">none</span>}</td>
                        <td className="num">{e.available ? e.services : <span className="muted">release not readable</span>}</td>
                        <td className="hide-phone mono small">{e.database ?? <span className="muted">none</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="panel flush" aria-labelledby="outside">
              <h2 id="outside" style={{ padding: "16px 20px 8px" }}>Outside the lab</h2>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Environment</th><th>Where</th><th>Found in AWS</th></tr></thead>
                  <tbody>
                    {d.external.map((e) => (
                      <tr key={`${e.project}/${e.env}`}>
                        <td><Link to={`/p/${e.project}?tab=architecture&env=${encodeURIComponent(e.env)}`} className="cell-main">{e.projectName}</Link><div className="cell-sub mono">{e.env}</div></td>
                        <td>{city(e.region)}<div className="cell-sub mono">{e.region}</div></td>
                        <td className="small">
                          {e.counts
                            ? [
                                e.counts.instances && plural(e.counts.instances, "instance"),
                                e.counts.services && plural(e.counts.services, "ECS service"),
                                e.counts.loadBalancers && plural(e.counts.loadBalancers, "load balancer"),
                                e.counts.distributions && plural(e.counts.distributions, "distribution"),
                                e.counts.databases && plural(e.counts.databases, "database"),
                                e.counts.buckets && plural(e.counts.buckets, "bucket"),
                                e.counts.names && plural(e.counts.names, "DNS name"),
                              ].filter(Boolean).join(" · ") || <span className="muted">nothing found</span>
                            : <span className="error">{e.error}</span>}
                          {e.errors.length > 0 && <div className="cell-sub">not read: {e.errors.join("; ")}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
          {d.graph.notes.length > 0 && (
            <section className="panel" aria-labelledby="sys-notes">
              <h2 id="sys-notes" style={{ marginBottom: 8 }}>Notes</h2>
              {d.graph.notes.map((n) => <p key={n} className="small muted" style={{ marginTop: 4 }}>{n}</p>)}
            </section>
          )}
        </>
      )}
    </div>
  );
}
